import { RuleId } from '@wix/a11y-rules';
import { Box } from '@wix/design-system';
import { useExperiments, useTranslation } from '@wix/fe-essentials/react';
import { GroupExpansionStyle, GroupType, IssueBadge, RuleListStatus } from '@wix/rules-panel-ui';
import { assertNotOptional } from '@wix/seo-common-core';
import { groupBy, omit } from 'lodash';
import React from 'react';
import { usePanelStateAtom } from '../../hooks/rules-state';
import { useColorContrastRule, useHeadingStructureRule, useValidStaticPageTitleRule } from '../../rule-mappers/page-level-rules';
import { useInaccessibleComponentsRule } from '../../rule-mappers/page-level-rules/use-inaccessible-components-rule';
import { useVerify } from '../../rule-mappers/shared/use-verify';
import { resolveGroupAnalysisState } from '../../utils/resolve-group-analysis-state';
import { CrossPageNotification } from '../verticals-drill-in/cross-page-notification';
import { RuleResultsSkeleton } from '../wizard-content/rule-results-skeleton';
import { VerifyPageButton } from '../wizard-content/verify-page-button';
export const useBuildSpecificPageRuleGroups = _ref => {
  let {
    ruleResults,
    itemType,
    tpaPageId,
    isAllCrossPageIssuePassed
  } = _ref;
  const {
    experiments
  } = useExperiments();
  const {
    getGroupData,
    panelState
  } = usePanelStateAtom();
  assertNotOptional(itemType, 'itemType');
  const {
    t
  } = useTranslation();
  const {
    getColorContrast
  } = useColorContrastRule();
  const {
    getHeadingStructure
  } = useHeadingStructureRule();
  const {
    getValidStaticPageTitleProps
  } = useValidStaticPageTitleRule(itemType);
  const {
    getInaccessibleComponents
  } = useInaccessibleComponentsRule();
  const {
    verify
  } = useVerify({});
  const ruleGroups = [];
  const ruleResultGroupByPage = groupBy(ruleResults, result => {
    var _result$metadata;
    return (_result$metadata = result.metadata) == null ? void 0 : _result$metadata.pageId;
  });
  const ruleResultGroupByInnerRoute = omit(ruleResultGroupByPage, tpaPageId);
  for (const [routerId, ruleExecutionResult] of Object.entries(ruleResultGroupByInnerRoute)) {
    var _resultsGroupedByRule, _resultsGroupedByRule2, _resultsGroupedByRule3, _getGroupData;
    const groupData = getGroupData(routerId);
    if (!groupData) {
      throw new Error(`Group data not found for pageId: ${routerId}.`);
    }
    const {
      analysisState
    } = groupData;
    const resultsGroupedByRule = groupBy(ruleExecutionResult, result => result.ruleId);
    const validStaticPageRuleResults = (_resultsGroupedByRule = resultsGroupedByRule[RuleId.VALID_STATIC_PAGE_TITLE]) == null ? void 0 : _resultsGroupedByRule[0];
    const headingStructurePageRuleResults = (_resultsGroupedByRule2 = resultsGroupedByRule[RuleId.HEADING_STRUCTURE]) == null ? void 0 : _resultsGroupedByRule2[0];
    const colorContrastRuleResults = (_resultsGroupedByRule3 = resultsGroupedByRule[RuleId.COLOR_CONTRAST]) == null ? void 0 : _resultsGroupedByRule3[0];
    const inaccessibleComponentsResults = resultsGroupedByRule[RuleId.INACCESSIBLE_COMPONENTS];
    const uiRuleResultsById = {
      [RuleId.VALID_STATIC_PAGE_TITLE]: validStaticPageRuleResults ? getValidStaticPageTitleProps({
        pageId: tpaPageId,
        routerId,
        ruleExecutionResult: validStaticPageRuleResults
      }) : null,
      [RuleId.HEADING_STRUCTURE]: headingStructurePageRuleResults ? getHeadingStructure({
        pageId: tpaPageId,
        routerId,
        ruleExecutionResult: headingStructurePageRuleResults
      }) : null,
      [RuleId.COLOR_CONTRAST]: colorContrastRuleResults ? getColorContrast({
        pageId: tpaPageId,
        routerId,
        ruleExecutionResult: colorContrastRuleResults
      }) : null,
      [RuleId.MEDIA_ALTERNATIVES]: null,
      [RuleId.STATIC_ALT_TEXT]: null,
      [RuleId.INACCESSIBLE_COMPONENTS]: inaccessibleComponentsResults ? getInaccessibleComponents({
        pageId: tpaPageId,
        routerId,
        ruleExecutionResult: inaccessibleComponentsResults
      }) : null,
      [RuleId.INACCESSIBLE_LAYOUT]: null,
      [RuleId.PAGE_AUTO_DOM_ORDER]: null
    };
    const filteredUiRuleResults = Object.fromEntries(Object.entries(uiRuleResultsById).filter(_ref2 => {
      let [, value] = _ref2;
      return value !== null && (!value.enabledByExperiment || experiments.enabled(value.enabledByExperiment));
    }));
    const {
      isAnalyzeFailed,
      isAnalyzing: isAnalyzingState,
      isFirstAnalysis,
      isPendingResults
    } = resolveGroupAnalysisState(analysisState);
    const isAnalyzing = isAnalyzingState || isFirstAnalysis;
    const handleVerifyClick = async () => {
      var _panelState$globalSec;
      await verify(groupData, [...Array.from(((_panelState$globalSec = panelState.globalSectionComponentsDataMap) == null ? void 0 : _panelState$globalSec.values()) ?? []).map(_ref3 => {
        let {
          componentId
        } = _ref3;
        return componentId;
      })]);
    };
    const ruleResultsForGroup = Object.values(filteredUiRuleResults);
    const numberOfIssues = ruleResultsForGroup.filter(result => result.status === RuleListStatus.failed).length;
    const ruleGroup = {
      expansionMode: GroupExpansionStyle.collapsible,
      groupId: routerId,
      groupType: GroupType.StaticPage,
      isLoading: isPendingResults || isAnalyzing,
      onGroupToggle: () => {},
      ruleGroupContent: {
        badge: isAnalyzeFailed ? /*#__PURE__*/React.createElement(IssueBadge, {
          label: t('accessibility-wizard.rule-group.issue-count-badge.label.scan-failed')
        }) : numberOfIssues !== 0 && !isAnalyzing ? /*#__PURE__*/React.createElement(IssueBadge, {
          label: t('accessibility-wizard.rule-group.issue-count-badge.label', {
            numberOfIssues
          })
        }) : undefined,
        collapsedNode: isAnalyzeFailed || isAnalyzingState ? /*#__PURE__*/React.createElement(Box, {
          padding: "0 3px"
        }, /*#__PURE__*/React.createElement(VerifyPageButton, {
          failedTaskCount: numberOfIssues,
          isLoading: isAnalyzingState,
          pageId: routerId,
          taskCount: ruleResults.length,
          onClick: handleVerifyClick
        })) : undefined,
        expandNode: /*#__PURE__*/React.createElement(VerifyPageButton, {
          failedTaskCount: numberOfIssues,
          isLoading: isAnalyzingState,
          pageId: routerId,
          taskCount: ruleResults.length,
          onClick: handleVerifyClick
        }),
        groupLabel: ((_getGroupData = getGroupData(routerId)) == null ? void 0 : _getGroupData.title) ?? '',
        rulesListHeader: !isAllCrossPageIssuePassed && numberOfIssues === 0 ? /*#__PURE__*/React.createElement(CrossPageNotification, null) : undefined
      },
      ruleResults: ruleResultsForGroup,
      ruleResultsSkeletonComponent: /*#__PURE__*/React.createElement(RuleResultsSkeleton, {
        analysisState: analysisState,
        pageId: routerId,
        ruleResultsForGroup: ruleResultsForGroup
      })
    };
    ruleGroups.push(ruleGroup);
  }
  return ruleGroups;
};
//# sourceMappingURL=use-build-specific-page-rule-groups.js.map